<template>
  <GameMain
    v-if="isGameInitializedAndRunning"
    @game:over="handleGameOver"
  />

  <GameLoaderModal v-if="isGameLoaderModalOpen" />

  <BaseLoaderModal v-else-if="isLoaderModalOpen" />

  <PostGameModal
    v-else-if="isPostGameModalOpen"
    :info="postGameInfo"
    @start:newGame="handleStartNewGameFromPostGameModal"
    @redirect:storePage="handleRedirectToStorePageFromPostGameModal"
    @redirect:homePage="handleRedirectToHomePageFromPostGameModal"
  />

  <MaintenanceModal
    v-else-if="isMaintenanceModalOpen"
    @redirect:homePage="handleRedirectToHomePageFromMaintenanceModal"
  />

  <PreGameSection
    v-else-if="isPreGameVisible"
    @hide:preGame="handleHidePreGame"
  />
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import GameMain from '@/views/game/components/GameMain.vue';
import GameLoaderModal from '@/components/modal/loader/GameLoaderModal.vue';
import BaseLoaderModal from '@/components/modal/loader/BaseLoaderModal.vue';
import PostGameModal from '@/components/modal/game/PostGameModal.vue';
import MaintenanceModal from '@/components/modal/MaintenanceModal.vue';
import PreGameSection from '@/components/section/PreGameSection.vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useAuthStore from '@/store/modules/auth.js';
import useGameStore from '@/store/modules/game.js';
import usePageNavigationHook from '@/hooks/usePageNavigation.js';
import useSocketHook from '@/hooks/useSocket.js';
import useModalHook from '@/hooks/useModal.js';
import usePreGameHook from '@/hooks/usePreGame.js';
import { isPositiveNumber } from '@/helpers/common.js';
import { apiFinishGame } from '@/services/game.js';

const appSettingsStore = useAppSettingsStore();
const authStore = useAuthStore();
const gameStore = useGameStore();

const { redirectToHomePage, redirectToStorePage } = usePageNavigationHook();

const {
  createStompClient,
  connectStompClient,
  removeStompClient,
  subscribeToChannel,
  unsubscribeFromChannel,
} = useSocketHook(
  '/gameData',
  {
    finishGame: null,
  },
);

const [isGameLoaderModalOpen, openGameLoaderModal, closeGameLoaderModal] = useModalHook();
const [isLoaderModalOpen, openLoaderModal, closeLoaderModal] = useModalHook();
const [isPostGameModalOpen, openPostGameModal, closePostGameModal] = useModalHook();
const [isMaintenanceModalOpen, openMaintenanceModal, closeMaintenanceModal] = useModalHook();

const { isPreGameVisible, launchNewGameFlow, hidePreGame } = usePreGameHook();

const postGameInfo = ref({});
const isGameInitializedAndRunning = ref(false);

const resetGameStore = () => {
  gameStore.resetGameType();
  gameStore.resetGameId();
  gameStore.resetGameRoundList();
};

const clearSocket = async () => {
  unsubscribeFromChannel('finishGame');

  await removeStompClient();
};

const setupAndShowPostGame = (finishGameResponse) => {
  const totalQuestions = Array.isArray(finishGameResponse.listOfQuestions)
    ? finishGameResponse.listOfQuestions.length
    : 0;
  const correctAnswers = isPositiveNumber(finishGameResponse.correctQuestions)
    ? finishGameResponse.correctQuestions
    : 0;

  postGameInfo.value = {
    totalQuestions,
    correctAnswers,
    wrongAnswers: (totalQuestions > correctAnswers)
      ? totalQuestions - correctAnswers
      : 0,
    prizeValue: isPositiveNumber(finishGameResponse.userGameDetailsDTO?.postgamePointsTotal)
      ? finishGameResponse.userGameDetailsDTO.postgamePointsTotal
      : 0,
    availableGames: authStore.user.availableGames,
  };

  openPostGameModal({
    hideImmediate: true,
    afterHideCallback: redirectToHomePage,
  });
};

const stopLoaderAndShowMaintenanceModal = () => {
  isGameInitializedAndRunning.value = false;
  gameStore.setIsGameInProgress(false);

  closeLoaderModal({
    immediate: true,
    type: 'useCallback',
    afterHideCallback: () => {
      openMaintenanceModal({
        hideImmediate: true,
        afterHideCallback: redirectToHomePage,
      });
    },
  });
};

const connectToSocket = () => {
  return new Promise((resolve) => {
    connectStompClient(resolve);
  });
};

const subscribeToFinishGameChannel = () => {
  subscribeToChannel(
    'finishGame',
    `/topic/finishedGame/${ gameStore.gameId }`,
    async (parsedData) => {
      isGameInitializedAndRunning.value = false;
      gameStore.setIsGameInProgress(false);

      if (!isLoaderModalOpen) {
        openLoaderModal({ hideManually: true });
      }

      unsubscribeFromChannel('finishGame');

      await authStore.dispatchRetrieveAuthUserLoginDetails();

      if ((parsedData === null) || (parsedData === 'Maintenance')) {
        stopLoaderAndShowMaintenanceModal();
      } else {
        await closeLoaderModal({ immediate: true });
        setupAndShowPostGame(parsedData);
      }
    },
  );
};

const initializeGame = async () => {
  gameStore.setIsGameInProgress(true);

  openGameLoaderModal({ showImmediate: true, hideManually: true });

  createStompClient();

  await connectToSocket();

  isGameInitializedAndRunning.value = true;

  await closeGameLoaderModal();

  subscribeToFinishGameChannel();
};

const handleGameOver = async ({ completed }) => {
  if (isGameInitializedAndRunning.value) {
    isGameInitializedAndRunning.value = false;

    openLoaderModal({ hideManually: true });

    if (completed) {
      try {
        /** call triggers a socket message that will be handled by the 'subscribeToFinishGameChannel' subscription */
        await apiFinishGame({
          queryParams: { gameId: gameStore.gameId },
        });
      } catch {
        stopLoaderAndShowMaintenanceModal();
      }
    }
  }
};

const handleStartNewGameFromPostGameModal = async () => {
  await closePostGameModal({ immediate: true });

  resetGameStore();

  await clearSocket();

  launchNewGameFlow();
};

const handleRedirectToStorePageFromPostGameModal = async () => {
  await closePostGameModal({ immediate: true });

  redirectToStorePage();
};

const handleRedirectToHomePageFromPostGameModal = async () => {
  await closePostGameModal({ immediate: true });

  redirectToHomePage();
};

const handleRedirectToHomePageFromMaintenanceModal = async () => {
  await closeMaintenanceModal({ immediate: true });

  redirectToHomePage();
};

const handleHidePreGame = ({ canProceed }) => {
  hidePreGame();

  if (canProceed) {
    initializeGame();
  } else {
    gameStore.setIsGameInProgress(false);
  }
};

onMounted(() => {
  if (gameStore.isPreGameCompleted) {
    gameStore.isPreGameCompleted = false;

    if (appSettingsStore.isMusicEnabled) {
      appSettingsStore.pauseMusic();
    }

    initializeGame();
  } else {
    redirectToHomePage();
  }
});

onUnmounted(() => {
  resetGameStore();

  clearSocket();

  if (appSettingsStore.isMusicEnabled) {
    appSettingsStore.playMusic();
  }
});
</script>
